import React from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined
} from "@ant-design/icons";
import { Col, Row, Tag } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { formatDateTime, formatLeadSource } from "../../../../utils/functions";

const EnhancedLeadImportedUserDetails = ({
    leadDetail,
    setShowDetail,
    onToggleHotLead
}) => {
    if (!leadDetail) return null;

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Source: <InfoCircleOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    return (
        <div className="p-6" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                title={
                    <div className="flex items-center gap-2">
                        <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "18px" }} />
                        <span style={{ fontSize: "16px", fontWeight: 600 }}>Lead Details</span>
                    </div>
                }
                extra={
                    onToggleHotLead && (
                        <div
                            onClick={() => onToggleHotLead(leadDetail)}
                            className="cursor-pointer flex items-center gap-2 px-3 py-1 rounded-md transition-colors hover:bg-gray-100"
                        >
                            {leadDetail.isHotLead ? (
                                <Tag color="red" className="m-0 flex items-center gap-1 cursor-pointer">
                                    <UserOutlined /> Hot Lead
                                </Tag>
                            ) : (
                                <Tag className="m-0 flex items-center gap-1 cursor-pointer">
                                    <UserOutlined /> Mark as Hot Lead
                                </Tag>
                            )}
                        </div>
                    )
                }
            >
                <Row gutter={[24, 0]}>
                    {/* Photo Section (if available) */}
                    {leadDetail.photo && (
                        <Col span={24} className="border-b border-gray-200 py-4 text-center">
                            <img
                                src={leadDetail.photo}
                                alt="Lead"
                                width={100}
                                style={{ borderRadius: "50%", margin: "0 auto" }}
                            />
                        </Col>
                    )}

                    {/* Contact Details Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <div className="flex items-center gap-2 mb-4">
                            <UserOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Contact Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                {
                                    label: "Name",
                                    value: leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`
                                },
                                { label: "Email", value: leadDetail?.email },
                                { label: "Phone", value: leadDetail?.phone },
                                { label: "Address", value: leadDetail?.address },
                                { label: "Company", value: leadDetail?.company },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Event Details Section */}
                    <Col span={24} className={cn("py-4", !leadDetail?.inquiry && "border-b-0")}>
                        <div className="flex items-center gap-2 mb-4">
                            <CalendarOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                            <span style={{ fontSize: "15px", fontWeight: 600 }}>Event Information</span>
                        </div>
                        <Row gutter={[24, 16]}>
                            {[
                                { label: "Event Name", value: leadDetail?.eventName },
                                { label: "Event Type", value: leadDetail?.eventType },
                                { label: "Event Date", value: leadDetail?.eventDate },
                                { label: "Event Time", value: leadDetail?.eventTime },
                                { label: "Guest Count", value: leadDetail?.guestCount },
                                { label: "Imported At", value: formatDateTime(leadDetail?.createdAt) },
                                {
                                    label: "Source",
                                    value: formatLeadSource(leadDetail?.leadSource),
                                    isTag: true
                                },
                            ].map((item) => (
                                <Col span={12} key={item.label}>
                                    <div className="flex flex-col">
                                        <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                                            <span style={{ color: "var(--primary-color, #1890ff)" }}>
                                                {getIconForField(item.label)}
                                            </span>
                                            {item.label}
                                        </span>
                                        {item.isTag ? (
                                            <Tag color="blue" className="m-0 px-3 py-1 font-medium capitalize w-fit">
                                                {item.value || "—"}
                                            </Tag>
                                        ) : (
                                            <span className="text-sm font-semibold text-gray-900">{item.value || "—"}</span>
                                        )}
                                    </div>
                                </Col>
                            ))}
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="border-t border-gray-200 pt-4">
                            <div className="flex items-center gap-2 mb-3">
                                <InfoCircleOutlined style={{ color: "var(--primary-color, #1890ff)", fontSize: "16px" }} />
                                <span style={{ fontSize: "15px", fontWeight: 600 }}>Notes/Inquiry</span>
                            </div>
                            <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                        </Col>
                    )}
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedLeadImportedUserDetails;
